import * as React from 'react'
import { toBase64 } from 'utils/base64'

import type { UploadedFile } from '../../_hooks/use-image-upload'
import { ImageUploadInput } from './components/image-upload-input'

interface MultiImageProps {
  className?: string
  images: UploadedFile[]
  onFileDelete?: (index: number) => void
  onFileAdd?: (files: UploadedFile[]) => void
  name?: string
  id?: string
  alt?: string
  maxImages?: number
  onFilesReordered: (images: UploadedFile[]) => void
  setLoading?: (loading: boolean) => void
  buttonClassName?: string
  inputRef?: React.RefObject<HTMLInputElement | null>
}

export function ImageUpload({
  className,
  images,
  onFileDelete,
  onFileAdd,
  name,
  id = '',
  maxImages,
  onFilesReordered,
  setLoading,
  buttonClassName,
  inputRef,
}: MultiImageProps) {
  const [imageUrls, setImageUrls] = React.useState<Map<string, string>>(new Map<string, string>())

  React.useEffect(() => {
    const unifyImages = async () => {
      const result = new Map<string, string>()
      for (const image of images) {
        // If the image already has an imageUrl with no web fileSize (e.g., from editing existing discussion), use it
        // Otherwise convert the file to base64 for preview
        if (image.uploadedUrl && image.size === 0) {
          result.set(image.name, image.uploadedUrl)
        } else {
          result.set(image.name, await toBase64(image))
        }
      }
      setImageUrls(result)
    }
    unifyImages()
  }, [images])

  const handleDelete = (e: React.MouseEvent, index: number) => {
    e.stopPropagation()
    e.preventDefault()
    onFileDelete && onFileDelete(index)
  }

  const handleAdd = (files: UploadedFile[]) => {
    if (!maxImages || images.length < maxImages) {
      onFileAdd && onFileAdd(files)
    }
  }

  return (
    <div className={className}>
      <ImageUploadInput
        handleFile={handleAdd}
        name={name}
        id={id}
        currentFileCount={images.length}
        images={images}
        onFilesReordered={onFilesReordered}
        handleDelete={handleDelete}
        imageUrls={imageUrls}
        setLoading={setLoading}
        buttonClassName={buttonClassName}
        maxImages={maxImages}
        inputRef={inputRef}
      />
    </div>
  )
}
